// src/pages/NotFoundPage.jsx
//
// WHY THIS EXISTS
// Unmatched paths under /:lang used to render <Navigate to=".." replace />, silently
// bouncing the visitor to the language homepage. For a crawler that reads as: every
// URL on this domain returns 200 OK with the homepage <title> and zero body text.
// A live check of /en/this-page-does-not-exist-xyz returned exactly that — an infinite
// soft-404 surface. See doc/SEO_MOCK_EXAM_RANKING_DIAGNOSIS_2026-08.md §4.
//
// Firebase Hosting cannot return a 404 status for a path its SPA rewrite claims, so the
// authoritative signal we CAN send is a robots noindex, which Google honours after
// rendering. firebase.json narrows the rewrite so genuinely non-app paths get a real
// 404.html with a 404 status; this component covers /:lang/<unknown>, which must keep
// rewriting because the SPA owns that space.
//
// The page also does useful link work: it is a crawlable exit into the main sections
// rather than a dead end.

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SmartLink from '../components/ui/SmartLink';

const LINKS = [
  { to: '/', key: 'notFound.linkHome', fallback: 'Home' },
  { to: '/mock-exams', key: 'notFound.linkMockExams', fallback: 'German mock exams' },
  { to: '/blog', key: 'notFound.linkBlog', fallback: 'Exam guides & blog' },
  { to: '/pricing', key: 'notFound.linkPricing', fallback: 'Pricing' },
  { to: '/faq', key: 'notFound.linkFaq', fallback: 'FAQ' },
  { to: '/contact', key: 'notFound.linkContact', fallback: 'Contact' },
];

export default function NotFoundPage() {
  const { t } = useTranslation();

  // Tell crawlers not to index this URL, and clean up on unmount so the tag can never
  // leak onto a real page during client-side navigation.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, follow');
    meta.setAttribute('data-notfound', 'true');
    document.head.appendChild(meta);
    return () => {
      document.querySelectorAll('meta[data-notfound="true"]').forEach((el) => el.remove());
    };
  }, []);

  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-16">
      <div className="max-w-lg w-full text-center">
        <p
          className="text-[11px] font-semibold tracking-[0.16em] uppercase text-muted-foreground mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {t('notFound.badge', 'Error 404')}
        </p>
        <h1
          className="text-3xl sm:text-4xl font-extrabold mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {t('notFound.title', 'This page does not exist')}
        </h1>
        <p className="text-[15px] text-muted-foreground leading-relaxed mb-10">
          {t(
            'notFound.body',
            'The link may be broken or the page may have moved. Here is where everything else lives.'
          )}
        </p>

        <nav aria-label={t('notFound.navLabel', 'Site sections')}>
          <ul className="flex flex-wrap justify-center gap-2.5">
            {LINKS.map((link) => (
              <li key={link.to}>
                <SmartLink
                  to={link.to}
                  className="inline-flex items-center min-h-[48px] px-5 rounded-xl border border-border bg-card text-[14px] font-medium hover:bg-muted transition-colors duration-200 touch-manipulation active:scale-[0.98]"
                >
                  {t(link.key, link.fallback)}
                </SmartLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </main>
  );
}
